import { useEffect, useRef } from "react";
import { stepDependenciesQueryOptions } from "~/components/build/Show/lib/queries/stepDependencies";
import { useQuery } from "@tanstack/react-query";
import { Build, Step, StepDependencyContext } from "~/components/build/Show/lib/types";
import { useStepDependenciesStore } from "~/components/build/Show/lib/useStepDependenciesStore";

type UseStepDependenciesReturn = {
  dependencies: StepDependencyContext["dependencies"];
  missingDependencies: StepDependencyContext["missing_dependencies"];
};

export function useStepDependencies(
  stepUuid: Step["uuid"] | null | undefined,
): UseStepDependenciesReturn {
  const stepDeps = useStepDependenciesStore((state) =>
    stepUuid ? state.dependenciesByStep[stepUuid] : undefined,
  );

  if (!stepUuid) {
    return {
      dependencies: [],
      missingDependencies: [],
    };
  }

  const { dependencies = [], missing_dependencies: missingDependencies = [] } = stepDeps || {};

  return {
    dependencies,
    missingDependencies,
  };
}

/**
 * Ensure the step dependency graph is fetched and stored in the useStepDependenciesStore.
 * Sets a data attribute on the body element for test environments.
 */
export function useStepDependenciesQuery(build: Build) {
  const stepDependenciesLoaded = useRef(false);
  const { data: stepDependencies } = useQuery(
    stepDependenciesQueryOptions(build, build.stepUploadsCount),
  );

  useEffect(() => {
    if (!stepDependenciesLoaded.current) {
      stepDependenciesLoaded.current = true;
      document.body.setAttribute("data-step-dependencies-loaded", "true");
    }
  }, [stepDependencies]);
}
